import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState } from '../stores/types/types';
import { FkTable } from '../stores/types/content';
import {
	PptxDeckSlideInfo, pptxDeckToDisplayOrder, pptxDeckAlignSlides,
} from '../utils/pptx-deck-animations-reconcile';

const PPTX_DECK_CONTENT_TYPE = 'pptx-deck';
const EMPTY_SLIDES: PptxDeckSlideInfo[] = [];

interface PptxDeckAnimations {
	hasAnimations: boolean;
	contentUrl: string | undefined;
	/**
	 * The deck's AllInfo slides (physical DOP order), remapped to the saved
	 * display order (`media_data.slides`) — every consumer of per-slide builds
	 * (has-animation icons, next/prev targets, the presentation iframe itself)
	 * should read this instead of selecting PptxDeckAnimationsReducer directly,
	 * which is always physical order regardless of any reorder.
	 */
	slides: PptxDeckSlideInfo[];
	/**
	 * Display position -> physical DOP slide index, for callers that need the
	 * raw mapping too. `null` at a position is a page added after conversion.
	 */
	slideOrder: (number | null)[] | undefined;
}

/**
 * Resolves whether a media item is a converted PPTX deck with animations to play.
 *
 * The deck-summary `gr_generated_content` row has `contentType === 'pptx-deck'`,
 * keyed to the media item via `fkTable === 'gr_media'` && `fkId === mediaId`; its
 * `data` carries `totalAnimations`. It's stored as a JSON string in the DB, but
 * the store delivers it already parsed to an object — handle both. When there's
 * at least one animation, the media renders as an iframe pointed at `contentUrl`
 * — whose shim player layer handles next/prev/jump regardless of conversion
 * provider — instead of a static image from `media_data.image_array`. A deck
 * with zero animations gains nothing from the iframe, so it stays on images.
 */
const usePptxDeckAnimations = (mediaId: number): PptxDeckAnimations => {
	const generatedContent = useSelector(
		(s: GreenroomState) => s.GeneratedContentReducer.generatedContent,
	);
	const rawSlides = useSelector(
		(s: GreenroomState) => s.PptxDeckAnimationsReducer.info[mediaId]?.slides,
	) ?? EMPTY_SLIDES;
	const mediaData = useSelector(
		(s: GreenroomState) => s.MediaReducer.media.find((m) => m.id === mediaId)?.media_data,
	);
	const savedSlides = mediaData?.slides;
	const pageCount = mediaData?.image_array?.length;

	const deckInfo = useMemo(() => {
		const row = generatedContent?.find(
			(x) => x.fkTable === FkTable.media
				// eslint-disable-next-line eqeqeq
				&& x.fkId == mediaId // loose: fkId can arrive as a string (matches use-media-memos)
				&& x.contentType === PPTX_DECK_CONTENT_TYPE,
		);
		let totalAnimations = 0;
		const rawData = row?.data as unknown;
		if (typeof rawData === 'string') {
			try {
				totalAnimations = JSON.parse(rawData)?.totalAnimations ?? 0;
			} catch {
				totalAnimations = 0;
			}
		} else if (rawData && typeof rawData === 'object') {
			totalAnimations = (rawData as { totalAnimations?: number }).totalAnimations ?? 0;
		}
		return {
			hasAnimations: !!row?.contentUrl && totalAnimations > 0,
			contentUrl: row?.contentUrl,
		};
	}, [generatedContent, mediaId]);

	// Sized to image_array (the pages users see), so pages added before `slides`
	// was kept in step with uploads still read as added pages, not DOP slides.
	const slideOrder = useMemo(
		() => savedSlides && pptxDeckAlignSlides(savedSlides, pageCount ?? savedSlides.length)
			.map((s) => s?.index ?? null),
		[savedSlides, pageCount],
	);
	const slides = useMemo(
		() => pptxDeckToDisplayOrder(rawSlides, slideOrder),
		[rawSlides, slideOrder],
	);

	return {
		...deckInfo, slides, slideOrder,
	};
};

export default usePptxDeckAnimations;
